import React, { useState, useEffect, useRef } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEdit,
  FaExclamationTriangle,
  FaPlus,
  FaRegTrashAlt,
} from "react-icons/fa";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import CreateTransactionModal from "./createTransaction";
import {
  deleteTransaction,
  getFutureTransaction,
  getTransaction,
  getUserAssets,
} from "../../../../../services/transaction";
import CustomDropdown from "../../../../../components/Dropdown";
import { formatNumber, truncateText } from "../../../../../utility/helper";
import moment from "moment";
import { filterDropdownOptions } from "../../../../../utility/data";
import ConfirmDialog from "../../../../../components/ConfirmDialog";
import {
  clearSelectedAsset,
  fetchUserAssets,
  setSelectedAsset,
} from "../../../../../redux/slices/userAssetsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const ViewTransaction = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [type, setType] = useState("");
  const [viewTransaction, setViewTransaction] = useState([]);
  const [viewFutureTransaction, setViewFutureTransaction] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState(filterDropdownOptions[0]);
  const [singleTransactionData, setSingleTransactionData] = useState({});
  const [actionTypes, setActionTypes] = useState("Create");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});
  const userAssets = useSelector((state) => state.userAssets.data);
  const selectedAsset = useSelector((state) => state.userAssets.selectedAsset);
  const toast = useRef(null);
  const dropdownOptions = [
    { name: "INCOME" },
    { name: "EXPENSE" },
    { name: "IN" },
    { name: "OUT" },
  ];
  const dispatch = useDispatch();
  const accessToken = JSON.parse(localStorage.getItem("access_token"));
  const location = useLocation();
  const id = location?.state?.id || null;

  useEffect(() => {
    if (selectedAsset) {
      GetTransactionData();
      GetFutureTransactionData();
    }
  }, [selectedAsset, filterType, page]);

 useEffect(() => {
    dispatch(fetchUserAssets({ assetTypeId: 1, selectedAssetId: id }));
    return () => {
      dispatch(clearSelectedAsset());
    };
  }, [dispatch, id]);

  const openConfirmDialog = (config) => {
    setDialogConfig(config);
    setIsDialogOpen(true);
  };

  const confirmTransactionDelete = (transactionId) => {
    openConfirmDialog({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this transaction?",
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelClass:
        "bg-primary hover:border-white text-gray-300 font-bold py-2 px-4 rounded border border-[rgb(161,147,147)]",
      confirmClass:
        "bg-secondary hover:bg-gray-400 font-bold py-2 px-4 rounded ml-4 text-primary border",
      onConfirm: () => handleTransactionDelete(transactionId),
    });
  };

  const handleTransactionDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem("access_token"));
      const response = await deleteTransaction(accessToken, user?.id);
      if (response) {
        fetchUserAssets();
        GetTransactionData();
        GetFutureTransactionData();
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: "transaction deleted successfully",
          life: 3000,
        });
      }
    } catch (err) {
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: err.message,
        life: 3000,
      });
    }
  };

  const GetTransactionData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTransaction(
        selectedAsset.id,
        1,
        page * limit,
        limit,
        filterType,
        accessToken
      );
      if (response.status === 200) {
        setViewTransaction(response?.data.data);
      } else {
        throw new Error("Failed to fetch user data");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const GetFutureTransactionData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFutureTransaction(
        selectedAsset.id,
        page,
        limit,
        filterType,
        1,
        accessToken
      );
      if (response.status === 200) {
        setViewFutureTransaction(response?.data.data);
      } else {
        throw new Error("Failed to fetch user data");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option, actionTypes, data) => {
    setModalVisible(true);
    setType(option);
    setActionTypes(actionTypes);
    setSingleTransactionData(data);
  };

  const dateTemplate = (rowData) => {
    return moment(rowData.date).format("DD-MM-YYYY");
  };

  const currencyTemplate = (rowData) => {
    return rowData.date ? rowData.currency : "";
  };

  const LinkedAssetTemplate = (rowData) => {
    return rowData.asset_name ? (
      <div className="text-secondary">
        {truncateText(rowData.asset_name, 19)}{" "}
      </div>
    ) : (
      ""
    );
  };

  const reasonTemplate = (rowData) => {
    if (rowData.isFuture) {
      return "";
    }

    return rowData.remark ? rowData.remark : "-";
  };

  const cashTemplate = (rowData) => {
    const cashValue = rowData.amount;
    const type = rowData.type?.toUpperCase();

    return type === "OUT" || type === "EXPENSE" ? (
      <div className="text-expense">-{formatNumber(cashValue)}</div>
    ) : type === "IN" || type === "INCOME" ? (
      <div className="text-secondary">{formatNumber(cashValue)}</div>
    ) : null;
  };

  const balanceTemplate = (rowData) => {
    if (rowData.for_future) {
      return "-";
    }
    const cashValue = rowData.balance;

    if (typeof cashValue === "number") {
      return formatNumber(cashValue);
    }

    return null;
  };

  const actionBodyTemplate = (rowData) => {
    if (rowData?.isFuture) {
      return null;
    }

    return (
      <div className="flex gap-2">
        <button
          disabled={rowData?.is_initial}
          className={`focus:outline-none ${
            rowData?.is_initial
              ? "text-gray-400 cursor-not-allowed"
              : "text-secondary"
          }`}
        >
          <FaEdit
            onClick={() => {
              if (!rowData?.is_default) {
                handleOptionSelect(rowData.type, "Update", rowData);
              }
            }}
          />
        </button>
        <button
          disabled={rowData?.is_initial}
          className={`focus:outline-none ${
            rowData?.is_initial
              ? "text-gray-400 cursor-not-allowed"
              : "text-expense"
          }`}
        >
          <FaRegTrashAlt
            onClick={() => {
              if (!rowData?.is_default) {
                confirmTransactionDelete(rowData);
              }
            }}
          />
        </button>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        {...dialogConfig}
      />
      <div className="">
        <div className="grid md:grid-cols-8 grid-cols-4 gap-6 px-3 pt-7">
          <div className="md:col-span-4 col-span-5">
            <div className="grid md:grid-cols-3 grid-cols-3 gap-4 text-[.70rem] font-bold">
              <div className="mt-[-29px] text-gray-400">
                <CustomDropdown
                  options={userAssets}
                  name={selectedAsset ? selectedAsset.name : "Select Asset"}
                  assetId={selectedAsset ? selectedAsset.id : "Select Asset"}
                  onOptionSelect={(option) =>
                    dispatch(setSelectedAsset(option))
                  }
                  returnFullObject={true}
                  truncateText={truncateText}
                  size={19}
                  className="inline-flex whitespace-nowrap justify-center px-1 py-1.5  items-center gap-1 text-[0.65rem] font-medium bg-transparent rounded-md hover:border hover:border-secondary"
                />
              </div>
              <div className="text-secondary font-black col-span-2 text-[.85rem]">
                <span className="mr-4">CHF</span>{" "}
                {formatNumber(
                  viewTransaction?.remaining_balance
                    ? viewTransaction?.remaining_balance
                    : 0
                )}
              </div>
              <div className="grid grid-cols-subgrid gap-4 col-span-3 whitespace-nowrap font-black mt-[-43px] pl-1">
                <div className="mt-2 text-[.85rem]">
                  {viewTransaction?.asset_name
                    ? truncateText(viewTransaction.asset_name, 19)
                    : "Asset Name"}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-4 col-span-3 text-[.70rem] font-bold">
            <div className="grid md:grid-cols-4 grid-cols-4 ">
              <div className="">INCOME</div>
              <div className="text-secondary">
                <span className="mr-4">CHF</span>{" "}
                {formatNumber(
                  viewTransaction?.total_income
                    ? viewTransaction?.total_income
                    : 0
                )}
              </div>
              <div className="">IN</div>
              <div className="text-secondary">
                <span className="mr-4">CHF</span>{" "}
                {formatNumber(
                  viewTransaction?.total_in ? viewTransaction?.total_in : 0
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-4 grid-cols-4 mt-4">
              <div className="">EXPENSE</div>
              <div className="text-expense">
                <span className="mr-4">CHF</span>{" "}
                {formatNumber(
                  viewTransaction?.total_expense
                    ? -parseFloat(viewTransaction?.total_expense) || 0
                    : 0
                )}
              </div>
              <div className="">OUT</div>
              <div className="text-expense">
                <span className="mr-4">CHF</span>{" "}
                {formatNumber(
                  viewTransaction?.total_out ? viewTransaction?.total_out : 0
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute right-5 top-[6.5rem]">
          <CustomDropdown
            options={dropdownOptions}
            name={""}
            assetId={""}
            icon={<FaPlus />}
            direction={"right"}
            truncateText={truncateText}
            size={19}
            className="flex items-center justify-center w-12 h-12 rounded-full font-bold bg-secondary text-primary hover:bg-[#148f77]"
            onOptionSelect={(e) => handleOptionSelect(e, "Create")}
          />
        </div>
        <div className="border-t border-[rgb(35,135,159)] font-bold mt-6">
          <span className="pl-4 text-secondary">Movements</span>
        </div>
        {/* <div className='flex flex-col gap-2'>
        <label>Type</label>
        <Dropdown
                value={filterType}
                name="groupName"
                label="Type"
                options={filterDropdownOptions}
                onChange={(e) => setFilterType(e.value)}
                optionLabel="name"
                className={`h-1/4 w-[150px] text-xs mb-2 p-0 border bg-transparent text-white rounded-lg focus:outline-none focus:border-b-2 ${assetName ? "asset_Dropdown" : ""}`}
              />
        {error && <p className="text-red-500">{error}</p>}
        </div> */}
        <DataTable
          value={viewTransaction?.transactions}
          className="transaction mt-3"
          sortMode="multiple"
          // paginator
          rows={limit}
          totalRecords={viewTransaction?.total}
          first={page * limit}
          // onPage={(e) => {
          //   setPage(e.page);
          //   setLimit(e.rows);
          // }}
          loading={loading}
          emptyMessage="No data available"
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="date"
            header="Date"
            body={dateTemplate}
            style={{ width: "10%" }}
          />
          <Column
            field="type"
            header="Transaction Type"
            style={{ width: "14%" }}
          />
          <Column
            field="remark"
            header="Reason"
            body={reasonTemplate}
            style={{ width: "12%" }}
          />
          <Column
            field="asset_name"
            header="Linked Asset"
            body={LinkedAssetTemplate}
            style={{ width: "11%" }}
          />
          <Column
            field="currency"
            header="Currency"
            body={currencyTemplate}
            style={{ width: "8%" }}
          />
          <Column
            field="asset_type_name"
            header="Asset"
            style={{ width: "12%" }}
          />
          <Column
            field="amount"
            header="Cash"
            body={cashTemplate}
            align="right"
            style={{ width: "13%" }}
          />
          <Column
            field="balance"
            header="Balance"
            body={balanceTemplate}
            align="right"
            style={{ width: "12%" }}
          />
          <Column
            field="Action"
            header="Action"
            body={actionBodyTemplate}
            style={{ width: "12%" }}
          />
        </DataTable>
        {viewTransaction?.total > limit && (
          <div
            className={`flex gap-3 items-center px-3 pt-10 text-secondary ${
              page > 0 ? "justify-between" : "justify-end"
            }`}
          >
            {page > 0 && (
              <button
                onClick={() => {
                  setPage((prevPage) => prevPage - 1);
                }}
                className="flex items-center gap-1"
              >
                <FaArrowLeft />
                View Previous
              </button>
            )}
            <button
              onClick={() => {
                const maxPage = Math.ceil(viewTransaction?.total / limit) - 1;
                if (page < maxPage) {
                  setPage((prevPage) => prevPage + 1);
                }
              }}
              className="flex items-center gap-1"
            >
              View Next
              <FaArrowRight />
            </button>
          </div>
        )}

        <div className="border-b border-[rgb(35,135,159)] mt-16">
          Future Cash Flow
        </div>
        <DataTable
          value={viewFutureTransaction?.transactions}
          className="transaction headerDisble"
          sortMode="multiple"
          // paginator
          rows={limit}
          totalRecords={viewFutureTransaction?.total}
          first={page * limit}
          // onPage={(e) => {
          //   setPage(e.page);
          //   setLimit(e.rows);
          // }}
          // loading={loading}
          emptyMessage="No data available"
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="date"
            header="Date"
            body={dateTemplate}
            style={{ width: "10%" }}
          />
          <Column
            field="type"
            header="Transaction Type"
            body={() => {
              return " ";
            }}
            style={{ width: "14%" }}
          />
          <Column
            field="remark"
            header="Reason"
            body={reasonTemplate}
            style={{ width: "12%" }}
          />
          <Column
            field="asset_name"
            header="Linked Asset"
            body={() => {
              return " ";
            }}
            style={{ width: "11%" }}
          />
          <Column
            field="currency"
            header="Currency"
            body={currencyTemplate}
            style={{ width: "8%" }}
          />
          <Column
            field="asset_type_name"
            header="Asset"
            body={() => {
              return " ";
            }}
            style={{ width: "12%" }}
          />
          <Column
            field="amount"
            header="Cash"
            body={cashTemplate}
            align="right"
            style={{ width: "13%" }}
          />
          <Column
            field="balance"
            header="Balance"
            body={() => {
              return "-";
            }}
            align="right"
            style={{ width: "12%" }}
          />
          <Column
            field="Action"
            header="Action"
            body={actionBodyTemplate}
            style={{ width: "12%" }}
          />
        </DataTable>
        <CreateTransactionModal
          visible={modalVisible}
          singleTransactionData={singleTransactionData}
          actionTypes={actionTypes}
          fetchTransactionData={GetTransactionData}
          fetchFutureTransactionData={GetFutureTransactionData}
          viewTransaction={viewTransaction}
          onHide={() => {
            setModalVisible(false);
          }}
          SelectType={type}
          toast={toast}
          loading={loading}
          setLoading={setLoading}
          assetName={selectedAsset}
        />
      </div>
    </>
  );
};

export default ViewTransaction;
